/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListJsonWebKeysRequest,
  ListJsonWebKeysSecurity,
} from "../models/operations/listjsonwebkeys.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListJsonWebKeysQuery,
  ListJsonWebKeysQueryData,
  prefetchListJsonWebKeys,
  queryKeyListJsonWebKeys,
} from "./listJsonWebKeys.core.js";
export {
  buildListJsonWebKeysQuery,
  type ListJsonWebKeysQueryData,
  prefetchListJsonWebKeys,
  queryKeyListJsonWebKeys,
};

export type ListJsonWebKeysQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listKeys jsonWebKeySets
 *
 * @remarks
 * List a JSON Web Key Set's published keys, newest first. Revoked keys drop out of the default listing; pass include_revoked to see the set's full revocation history. Requires org:read.
 */
export function useListJsonWebKeys(
  request: ListJsonWebKeysRequest,
  security?: ListJsonWebKeysSecurity | undefined,
  options?: QueryHookOptions<
    ListJsonWebKeysQueryData,
    ListJsonWebKeysQueryError
  >,
): UseQueryResult<ListJsonWebKeysQueryData, ListJsonWebKeysQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListJsonWebKeysQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listKeys jsonWebKeySets
 *
 * @remarks
 * List a JSON Web Key Set's published keys, newest first. Revoked keys drop out of the default listing; pass include_revoked to see the set's full revocation history. Requires org:read.
 */
export function useListJsonWebKeysSuspense(
  request: ListJsonWebKeysRequest,
  security?: ListJsonWebKeysSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListJsonWebKeysQueryData,
    ListJsonWebKeysQueryError
  >,
): UseSuspenseQueryResult<ListJsonWebKeysQueryData, ListJsonWebKeysQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListJsonWebKeysQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListJsonWebKeysData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      setId: string;
      includeRevoked?: boolean | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: ListJsonWebKeysQueryData,
): ListJsonWebKeysQueryData | undefined {
  const key = queryKeyListJsonWebKeys(...queryKeyBase);

  return client.setQueryData<ListJsonWebKeysQueryData>(key, data);
}

export function invalidateListJsonWebKeys(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      setId: string;
      includeRevoked?: boolean | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "jsonWebKeySets", "listKeys", ...queryKeyBase],
  });
}

export function invalidateAllListJsonWebKeys(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "jsonWebKeySets", "listKeys"],
  });
}
